// Profil-Nachbau F-13 (Bennys Freigabe 14.10.2026; AUFTRAG-PF-723, SPEC-WELLE-A WA-20, WA-21 und Abschnitt 9): die Wörter der
// Mitgliedsseite …/organisation/mitglieder/:userId – Statuszeile, „Rolle und Platz“, „Teams“ und „Was darf {Vorname}?“ mit Antwort,
// Grund und Weg je Funktion. Reine Funktionen über die Antwort von GET …/members/:userId/was-darf (Server organization/wasDarf.ts,
// PF-721), GET …/teams/zuordnung (WB-27) und GET /billing/kontingent/mitglieder; sie entscheiden nichts (M-01.6). Je Quelle eines
// Grunds und je Zustand der Recherche eine Funktion; eine unbekannte Quelle (Welle B ergänzt 'TEAM') und ein unbekannter Zustand
// sagen „Über eine weitere Regel der Firma.“, damit keine Zeile leer bleibt. Ein Weg zum Ändern steht nur, wenn die betrachtende
// Person ihn darf (Betrachter); in einer Sicht gibt es keine schreibenden Wege. Kein Betrag: das Kontingent nur in Prozent.
// Geprüft in frontend/tests/welle-a-mitglieder.test.mjs.
import type { PausiertVon } from '../api';
import type { Anteil, Balken } from '../kontingent';
import { anteilText } from '../billing/kontingentSaetze';
import { prozentAnzeige } from '../billing/prozent';
import { datumKurz, MEHR_GEBEN, pauseZusatz, PLATZ_GEBEN } from '../mitgliedTexte';
import { BESITZER, RECHTE_ANPASSEN } from '../rollenWort';
import type { Zuordnung } from '../teams/vertrag';
import { BEREICH_RECHT, bereichsTitel, FIRMEN_ANSICHTEN, firmenAnsichtAdresse, ORGANISATION_PFAD, organisationsAdresse } from '../../pages/einstellungen/seiten';
import type { Bereich } from '../../pages/einstellungen/seiten';

// ---- Vertrag mit dem Server (Spiegel von backend/src/administration/organization/wasDarf.ts) ----------------------------------

// Quelle, Zustand und Grund sind offene Aufzählungen (SPEC-WELLE-A 9 Punkt 1, als string): ein Wert, den diese Oberfläche noch
// nicht kennt, bekommt den Satz der weiteren Regel.
export type WasDarfQuelle = 'ROLLE' | 'UNTERGRENZE' | 'BESITZ' | 'ZUSATZROLLE' | 'ABGELEITET' | 'KEINE_ZUSATZROLLE' | 'EINGESCHRAENKT' | 'NICHT_IN_ROLLE';
export type WasDarfGrund = { quelle: string; rolle?: string };
export type WasDarfRecht = { name: string; ja: boolean; gruende: WasDarfGrund[] };
export type RechercheZustand = 'ACTIVE' | 'NO_SEAT' | 'ACCESS_BLOCKED' | 'PAYMENT_OVERDUE' | 'ORGANIZATION_BLOCKED' | 'EXPIRED' | 'RESEARCH_LIMIT'
  | 'COST_LIMIT' | 'COST_UNCONFIGURED' | 'AVV_REQUIRED' | 'PLATFORM_BLOCKED';
export type KostenGrund = 'PERSON' | 'FIRMA' | 'TEAM' | 'GUTHABEN' | 'OBERGRENZE';
export type WasDarfPerson = { id: number; name: string; email: string;
  rolle: { art: 'BESITZER' | 'ADMIN' | 'MITGLIED'; eingeschraenkt: boolean; zusatzrolle: string | null };
  dabeiSeit: string | null; kontoSeit: string; zuletztAngemeldet: string | null;
  zugang: { aktiv: boolean; pausiertSeit: string | null; pausiertVon: PausiertVon | null }; platz: { aktiv: boolean; seit: string | null };
  zweiFaktor: { eingerichtet: boolean; verlangt: boolean; sso: boolean }; anbieterKonto: boolean };
export type WasDarfAntwort = { person: WasDarfPerson; recherche: { status: RechercheZustand | string; grund: KostenGrund | string | null };
  rechte: WasDarfRecht[]; revision: number };
/** 404 MEMBER_NOT_FOUND: derselbe Satz wie der Server (wasDarf.ts). */
export const MEMBER_NOT_FOUND = 'Diese Person gehört nicht zu Ihrer Firma.';

export const wasDarfWeg = (organizationId: string, userId: number) => `/organizations/${encodeURIComponent(organizationId)}/members/${userId}/was-darf`;
export const zuordnungWeg = (organizationId: string) => `/organizations/${encodeURIComponent(organizationId)}/teams/zuordnung`;
/** Die Adresse der Mitgliedsseite (WA-23: der Name in der Liste führt hierher). */
export const mitgliedSeitenPfad = (userId: number) => `${organisationsAdresse('members')}/${userId}`;
/** Was die Liste der Mitgliedsseite im Zustand der Adresse mitgibt: ihre Abfrage (?zugang=…&rolle=…&sortierung=…&page=…), damit der
 *  Rückweg Filter und Seite behält (WA-20 Knopfzeile „Zurück zu „Mitglieder““). */
export type MitgliedOrt = { mitgliederAbfrage?: unknown };
/** Der Rückweg zur Liste mit ihrer Abfrage; nur eine Abfrage („?…“ ohne Anker), sonst die Liste ohne Abfrage. */
export function mitgliederRueckweg(ort: MitgliedOrt | null | undefined): string {
  const abfrage = ort?.mitgliederAbfrage;
  return `${organisationsAdresse('members')}${typeof abfrage === 'string' && /^\?[^#]*$/.test(abfrage) ? abfrage : ''}`;
}

// ---- Wörter -------------------------------------------------------------------------------------------------------------------

export const MITGLIED_TEXTE = {
  // Ersatz beim Laden (firmenRouten.tsx) und Kopf, solange die Antwort fehlt.
  titel: 'Mitglied',
  laden: 'Die Angaben werden geladen …',
  zurueck: `Zurück zu „${bereichsTitel('members')}“`,
  erneut: 'Erneut laden',
  rolleUndPlatz: 'Rolle und Platz',
  rolle: 'Rolle',
  zusatzrolle: 'Zusatzrolle',
  keineZusatzrolle: 'Keine',
  // Der Server nennt den Namen der Zusatzrolle einer anderen Person nur mit „Zusatzrollen ansehen“ (roles.read, PF-721).
  zusatzrolleOhneNamen: 'Ja; den Namen sehen Sie mit „Zusatzrollen ansehen“',
  lizenzplatz: 'Lizenzplatz',
  zweiFaktor: 'Zwei-Faktor-Anmeldung',
  teams: 'Teams',
  team: 'Team',
  keinTeam: 'kein eigenes Team',
  teamAdmin: 'Team-Admin',
  ja: 'ja',
  nein: 'nein',
  kiChampionVon: 'KI-Champion von',
  teamseite: 'Teamseite öffnen',
  wasDarfBeschreibung: 'Für jede Funktion: Antwort, Grund und wo Sie es ändern.',
  jaMarke: 'Ja',
  neinMarke: 'Nein',
  recherchieren: 'Recherchieren',
  kontingent: 'Kontingent',
  schluss: 'Profil, eigene Rechercheverläufe und die Hilfe stehen jeder Person der Firma zur Verfügung.',
  anbieterKonto: 'Konto des Anbieters: Die Rechte der Plattform stehen hier nicht.',
} as const;
export const wasDarfTitel = (vorname: string | null | undefined) => vorname?.trim() ? `Was darf ${vorname.trim()}?` : 'Was darf diese Person?';
export const nichtErlaubtTitel = (anzahl: number) => `Nicht erlaubt (${anzahl})`;
/** Der Satz für eine Quelle oder einen Zustand, den diese Oberfläche nicht kennt (SPEC-WELLE-A 9 Punkt 1). */
export const WEITERE_REGEL = 'Über eine weitere Regel der Firma.';
/** Wörtlich wie PLATFORM_RESEARCH_MESSAGE (backend/src/administration/customers.ts). */
export const PLATFORM_RESEARCH_MESSAGE = 'Betreiberkonten recherchieren nur mit Lizenzplatz in der internen Organisation. Support-Konten recherchieren nicht.';
// Wörter wie „Sicherheit der Firma“ in Firmendaten.tsx (ZWEI_FAKTOR, dort nicht exportiert).
const ZWEI_FAKTOR = { AKTIV: 'Eingerichtet', OFFEN: 'Nicht eingerichtet', SSO: 'Anmeldung über Ihr Firmenkonto (SSO)' } as const;
const SICHERHEIT_TITEL = FIRMEN_ANSICHTEN.find(a => a.ansicht === 'sicherheit')!.titel;

/** Die Wörter der Mandantenrechte, Spiegel von capabilityCatalog (backend/src/administration/capabilities.ts, scope 'tenant'), in
 *  der Reihenfolge des Katalogs. Der Test vergleicht beide; ein Recht ohne Wort zeigt seinen Namen. */
export const RECHT_WORT: Record<string, string> = {
  'organization.read': 'Firma ansehen',
  'members.read': 'Personen ansehen',
  'members.write': 'Mitgliedschaften und Plätze pflegen',
  'invitations.read': 'Einladungen ansehen',
  'invitations.write': 'Einladungen erstellen und verwalten',
  'billing.read': 'Abrechnung ansehen',
  'billing.write': 'Abrechnung und Abonnement ändern',
  'costs.read': 'Kontingent und Nutzung der Firma ansehen',
  'costs.write': 'Kontingent verteilen (Grenzen je Person)',
  'branding.read': 'Gestaltung ansehen',
  'branding.write': 'Gestaltung ändern',
  'audit.read': 'Änderungsprotokoll ansehen',
  'roles.read': 'Zusatzrollen ansehen',
  'roles.write': 'Rollen und Zuordnungen pflegen',
  'organization.write': 'Firmendaten und Anmelderegeln ändern',
  'policy.read': 'Nutzungsrichtlinie und Zustimmungen ansehen',
  'policy.write': 'Nutzungsrichtlinie ändern und veröffentlichen',
};
export const rechtWort = (name: string) => RECHT_WORT[name] ?? name;

// ---- Wege -----------------------------------------------------------------------------------------------------------------------

/** Ein Weg zum Ändern: ein Ziel in den Einstellungen der Firma oder ein vorhandenes Fenster der Seite (kein neuer Schreibweg). */
export type WegAktion = 'PLATZ_GEBEN' | 'FORTSETZEN' | 'MEHR_GEBEN' | 'RECHTE_ANPASSEN';
export type Weg = { text: string; ziel: string } | { text: string; aktion: WegAktion };
/** Je Funktion eine Zeile: ja null heißt „ohne Ja/Nein“ (Kontingent). */
export type Antwort = { key: string; funktion: string; ja: boolean | null; grund: string; weg: Weg | null };
/** Was die betrachtende Person darf: lesen ist die Lesemenge (in einer Sicht readable, sonst permissions), schreiben die Rechte
 *  ohne Sicht (in einer Sicht leer); selbst: die Seite zeigt das eigene Konto. */
export type Betrachter = { lesen: readonly string[]; schreiben: readonly string[]; selbst: boolean };
/** Die Begriffe der Firma (MK-1c) und der Vorname der Person (null: „diese Person“). F-23 (PF-NB-firma-3): rolle ist die Art der
 *  Rolle der Person aus der Antwort (person.rolle.art); beim Besitzer nennt der Grund der Quelle ROLLE „Besitzer“. */
export type Woerter = { admin: string; mitglied: string; vorname: string | null; rolle?: 'BESITZER' | 'ADMIN' | 'MITGLIED' };

const lesend = (b: Betrachter, recht: string, weg: Weg): Weg | null => b.lesen.includes(recht) ? weg : null;
const schreibend = (b: Betrachter, recht: string, weg: Weg): Weg | null => b.schreiben.includes(recht) ? weg : null;
const zu = (bereich: Bereich): Weg => ({ text: `Zu „${bereichsTitel(bereich)}“`, ziel: organisationsAdresse(bereich) });
// Die Seite „Verträge & AVV“ der Firma (PF-781; Leiste und Route trägt PF-786 ein).
const AVV_WEG: Weg = { text: 'Zu „Auftragsverarbeitung (AVV)“', ziel: `${ORGANISATION_PFAD}/vertraege` };
/** Die Teamseite in den Einstellungen der Firma (wie teamPfad in teams/teamApi.ts, ohne dessen Einbindungen). */
export const teamSeitenPfad = (teamId: string) => `${ORGANISATION_PFAD}/teams/${encodeURIComponent(teamId)}`;
const teamWeg = (teamId: string): Weg => ({ text: MITGLIED_TEXTE.teamseite, ziel: teamSeitenPfad(teamId) });
// Ein Plattformkonto ändert nur, wer zusätzlich Rollen pflegt (MemberTable darfAendern in Administration.tsx).
const darfPerson = (b: Betrachter, p: Pick<WasDarfPerson, 'anbieterKonto'>) => b.schreiben.includes('members.write') && (!p.anbieterKonto || b.schreiben.includes('roles.write'));

// ---- Statuszeile (WA-21) ----------------------------------------------------------------------------------------------------------

/** „Dabei seit 08.10.2026 · Zuletzt angemeldet am 12.10.2026“; ohne Beitrittsdatum „Konto seit …“ (WA-F15), nie angemeldet „Noch nie
 *  angemeldet“, bei einer Pause dazu „Pausiert seit …“. */
export function statusZeile(p: Pick<WasDarfPerson, 'dabeiSeit' | 'kontoSeit' | 'zuletztAngemeldet' | 'zugang'>): string {
  return [
    p.dabeiSeit ? `Dabei seit ${datumKurz(p.dabeiSeit)}` : `Konto seit ${datumKurz(p.kontoSeit)}`,
    p.zuletztAngemeldet ? `Zuletzt angemeldet am ${datumKurz(p.zuletztAngemeldet)}` : 'Noch nie angemeldet',
    p.zugang.aktiv ? null : p.zugang.pausiertSeit ? `Pausiert seit ${datumKurz(p.zugang.pausiertSeit)}` : 'Pausiert',
  ].filter(Boolean).join(' · ');
}

// ---- Rolle und Platz (WA-20) ------------------------------------------------------------------------------------------------------

/** „Eingerichtet“, „Nicht eingerichtet“ oder „Anmeldung über Ihr Firmenkonto (SSO)“, dazu „· von der Firma verlangt“. */
export function zweiFaktorWert(z: WasDarfPerson['zweiFaktor']): string {
  const wort = z.sso ? ZWEI_FAKTOR.SSO : z.eingerichtet ? ZWEI_FAKTOR.AKTIV : ZWEI_FAKTOR.OFFEN;
  return z.verlangt ? `${wort} · von der Firma verlangt` : wort;
}
/** Der Name der Zusatzrolle oder „Keine“. Ohne Namen, aber mit Gründen aus einer Zusatzrolle hat die Person eine, deren Namen die
 *  betrachtende Person nicht sieht; dann nie „Keine“. */
export function zusatzrolleWert(antwort: Pick<WasDarfAntwort, 'person' | 'rechte'>): string {
  if (antwort.person.rolle.zusatzrolle) return antwort.person.rolle.zusatzrolle;
  const mitRolle = antwort.person.rolle.eingeschraenkt
    || antwort.rechte.some(recht => recht.gruende.some(g => g.quelle === 'ZUSATZROLLE' || g.quelle === 'EINGESCHRAENKT' || g.quelle === 'NICHT_IN_ROLLE'));
  return mitRolle ? MITGLIED_TEXTE.zusatzrolleOhneNamen : MITGLIED_TEXTE.keineZusatzrolle;
}

// ---- Teams (SPEC-WELLE-A 9 Punkt 2, WB-27) ------------------------------------------------------------------------------------------

export type TeamWerte = { team: string; teamAdmin: string; kiChampionVon: string | null; teamId: string | null };
/** Die Zeilen des Abschnitts „Teams“ aus der Zuordnung der Firma; „KI-Champion von“ entfällt ohne Team (null). */
export function teamWerte(zuordnung: Zuordnung, userId: number): TeamWerte {
  const person = zuordnung.personen.find(eintrag => eintrag.userId === userId);
  return { team: person?.teamName ?? MITGLIED_TEXTE.keinTeam, teamAdmin: person?.teamAdmin ? MITGLIED_TEXTE.ja : MITGLIED_TEXTE.nein,
    kiChampionVon: person?.kiChampionVon.length ? person.kiChampionVon.join(', ') : null, teamId: person?.teamId ?? null };
}

// ---- „Recherchieren“ (WA-20 Tabelle 1) --------------------------------------------------------------------------------------------

export type RechercheLage = { person: Pick<WasDarfPerson, 'zugang' | 'anbieterKonto'>; grund: WasDarfAntwort['recherche']['grund'];
  /** das Team der Person aus der Zuordnung (für COST_LIMIT mit TEAM) */ teamId: string | null; b: Betrachter };
type Teil = Omit<Antwort, 'key' | 'funktion'>;

/** „Pausiert seit 07.10.2026 von Harald Weißhaupt (Firma).“ (Muster pauseZusatz); ohne Datum und ohne Urheber mit Semikolon. */
export function pausiertGrund(seit: string | null, von: PausiertVon | null): string {
  const bekannt = !!seit || von?.seite === 'ANBIETER' || (von?.seite === 'FIRMA' && !!von.name);
  return bekannt ? `Pausiert ${pauseZusatz(seit, von)}.` : `Pausiert; ${pauseZusatz(seit, von)}.`;
}

function kostenGrenze(l: RechercheLage): Teil {
  // Ohne Angabe gilt die Grenze der Person, wie am Server (researchUsage.ts assertAccess).
  const grund = l.grund ?? 'PERSON';
  if (grund === 'PERSON') return { ja: false, grund: 'Die persönliche Grenze ist erreicht.', weg: schreibend(l.b, 'costs.write', { text: MEHR_GEBEN, aktion: 'MEHR_GEBEN' }) };
  if (grund === 'TEAM') return { ja: false, grund: 'Das Kontingent des Teams ist aufgebraucht.', weg: l.teamId ? lesend(l.b, 'members.read', teamWeg(l.teamId)) : null };
  return { ja: false, grund: 'Das Kontingent der Firma ist aufgebraucht.', weg: lesend(l.b, 'costs.read', zu('cost')) };
}

/** Je Zustand der Recherche Antwort, Grund und Weg. */
export const RECHERCHE_JE_ZUSTAND: Record<RechercheZustand, (l: RechercheLage) => Teil> = {
  ACTIVE: () => ({ ja: true, grund: 'Hat einen Lizenzplatz; die Firma ist aktiv.', weg: null }),
  NO_SEAT: l => ({ ja: false, grund: 'Kein Lizenzplatz zugewiesen.', weg: darfPerson(l.b, l.person) ? { text: PLATZ_GEBEN, aktion: 'PLATZ_GEBEN' } : null }),
  // Ohne Pause ist das Konto selbst gesperrt (isActive, Plattform).
  ACCESS_BLOCKED: l => l.person.zugang.aktiv
    ? { ja: false, grund: 'Das Konto ist gesperrt. Entsperren kann nur der Anbieter von CLAiRIS.', weg: null }
    : { ja: false, grund: pausiertGrund(l.person.zugang.pausiertSeit, l.person.zugang.pausiertVon),
      weg: darfPerson(l.b, l.person) && !l.person.anbieterKonto ? { text: 'Fortsetzen …', aktion: 'FORTSETZEN' } : null },
  PAYMENT_OVERDUE: l => ({ ja: false, grund: 'Die Zahlung der Firma ist überfällig.', weg: lesend(l.b, 'billing.read', zu('billing')) }),
  ORGANIZATION_BLOCKED: () => ({ ja: false, grund: 'Die Firma ist gesperrt.', weg: null }),
  EXPIRED: l => ({ ja: false, grund: 'Die Lizenz ist abgelaufen.', weg: lesend(l.b, BEREICH_RECHT.plan, zu('plan')) }),
  RESEARCH_LIMIT: l => ({ ja: false, grund: 'Die Recherchen für diesen Monat sind aufgebraucht.', weg: lesend(l.b, 'costs.read', zu('cost')) }),
  COST_LIMIT: kostenGrenze,
  COST_UNCONFIGURED: () => ({ ja: false, grund: 'Für die Firma ist noch kein Kontingent eingestellt.', weg: null }),
  AVV_REQUIRED: l => ({ ja: false, grund: 'Der Auftragsverarbeitungsvertrag ist noch nicht angenommen.', weg: lesend(l.b, 'billing.read', AVV_WEG) }),
  PLATFORM_BLOCKED: () => ({ ja: false, grund: PLATFORM_RESEARCH_MESSAGE, weg: null }),
};
export function rechercheZeile(status: string, l: RechercheLage): Antwort {
  const zustand = (RECHERCHE_JE_ZUSTAND as Record<string, ((lage: RechercheLage) => Teil) | undefined>)[status];
  return { key: 'recherche', funktion: MITGLIED_TEXTE.recherchieren, ...(zustand ? zustand(l) : { ja: false, grund: WEITERE_REGEL, weg: null }) };
}

// ---- „Kontingent“ (nur mit costs.read, nie Euro) --------------------------------------------------------------------------------

/** Die Zeile der Person aus GET /billing/kontingent/mitglieder: „Persönliche Grenze: 4 % des Kontingents“ bzw. „Keine persönliche
 *  Grenze“, dazu „Diesen Monat verwendet: 2 % des Kontingents“ (ohne Kontingent der Firma entfällt der zweite Teil). */
export function kontingentZeile(eintrag: { grenzeProzent?: number | null; vomKontingent?: Anteil | null; kosten?: Balken | null }, b: Betrachter): Antwort {
  const grenze = typeof eintrag.grenzeProzent === 'number' ? `Persönliche Grenze: ${prozentAnzeige(eintrag.grenzeProzent)} des Kontingents`
    : eintrag.kosten ? 'Persönliche Grenze: —' : 'Keine persönliche Grenze';
  const verwendet = eintrag.vomKontingent ? `Diesen Monat verwendet: ${anteilText(eintrag.vomKontingent)} des Kontingents` : null;
  return { key: 'kontingent', funktion: MITGLIED_TEXTE.kontingent, ja: null, grund: [grenze, verwendet].filter(Boolean).join(' · '), weg: lesend(b, 'costs.read', zu('cost')) };
}

// ---- Die Verwaltungsrechte (WA-20 Tabelle 2) ----------------------------------------------------------------------------------------

const person = (w: Woerter) => w.vorname?.trim() || 'diese Person';
/** Je Quelle eines Grunds der Satz; die Begriffe der Firma nie fest. */
export const GRUND_JE_QUELLE: Record<WasDarfQuelle, (g: WasDarfGrund, w: Woerter) => string> = {
  // F-23 (PF-NB-firma-3, Fehlersuche Nr. 16): das Rollenwort wie „Mitglieder“ und „Rolle“ auf derselben Seite.
  ROLLE: (_g, w) => `Als ${w.rolle === 'BESITZER' ? BESITZER : w.admin} hat ${person(w)} alle Rechte der Firma.`,
  UNTERGRENZE: (_g, w) => `Bleibt jedem ${w.admin}, auch eingeschränkt.`,
  BESITZ: () => 'Bleibt der Besitzerin oder dem Besitzer immer.',
  ZUSATZROLLE: g => g.rolle ? `Über die Zusatzrolle „${g.rolle}“.` : 'Über die Zusatzrolle.',
  ABGELEITET: () => 'Gehört zu jeder Zusatzrolle.',
  EINGESCHRAENKT: g => g.rolle ? `Durch die Zusatzrolle „${g.rolle}“ eingeschränkt.` : 'Durch die Zusatzrolle eingeschränkt.',
  KEINE_ZUSATZROLLE: (_g, w) => `${w.mitglied} ohne Zusatzrolle.`,
  NICHT_IN_ROLLE: g => g.rolle ? `Nicht in der Zusatzrolle „${g.rolle}“.` : 'Nicht in der Zusatzrolle.',
};
export function grundSatz(g: WasDarfGrund, w: Woerter): string {
  const satz = (GRUND_JE_QUELLE as Record<string, ((grund: WasDarfGrund, woerter: Woerter) => string) | undefined>)[g.quelle];
  return satz ? satz(g, w) : WEITERE_REGEL;
}
/** „Rechte anpassen …“ öffnet „Mitglied verwalten“ (vorhandener Dialog) nur, wo das Feld dort steht und bedienbar ist: mit
 *  „Rollen und Zuordnungen pflegen“ und dem Recht, die Person zu ändern, nicht beim Besitzer, nicht bei einem Plattformkonto und nicht
 *  im eigenen Konto (forms.tsx MemberForm feldDa, rechteGrund). */
export function rechteAnpassenWeg(p: Pick<WasDarfPerson, 'rolle' | 'anbieterKonto'>, b: Betrachter): Weg | null {
  if (!b.schreiben.includes('roles.write') || !darfPerson(b, p) || p.rolle.art === 'BESITZER' || p.anbieterKonto || b.selbst) return null;
  return { text: `${RECHTE_ANPASSEN} …`, aktion: 'RECHTE_ANPASSEN' };
}
export function rechtZeile(recht: WasDarfRecht, w: Woerter, weg: Weg | null): Antwort {
  return { key: `recht:${recht.name}`, funktion: rechtWort(recht.name), ja: recht.ja,
    grund: recht.gruende.length ? recht.gruende.map(g => grundSatz(g, w)).join(' ') : WEITERE_REGEL, weg };
}

// ---- „Zwei-Faktor-Anmeldung“ ------------------------------------------------------------------------------------------------------

export function zweiFaktorZeile(z: WasDarfPerson['zweiFaktor'], b: Betrachter): Antwort {
  const grund = z.sso ? 'Anmeldung über das Firmenkonto (SSO).' : z.verlangt ? 'Von der Firma verlangt.'
    : z.eingerichtet ? 'Nicht verlangt; freiwillig eingerichtet.' : 'Nicht verlangt.';
  return { key: 'zwei-faktor', funktion: MITGLIED_TEXTE.zweiFaktor, ja: z.eingerichtet, grund,
    weg: lesend(b, 'organization.read', { text: `Zu „${SICHERHEIT_TITEL}“`, ziel: firmenAnsichtAdresse('sicherheit') }) };
}

// ---- Der ganze Abschnitt ----------------------------------------------------------------------------------------------------------

/** Recherchieren, Kontingent (nur mit costs.read und geladener Zeile), die Rechte in der Reihenfolge des Katalogs und die
 *  Zwei-Faktor-Anmeldung; erlaubt (Ja oder ohne Ja/Nein) steht offen, „Nicht erlaubt“ zugeklappt am Ende. */
export function wasDarfZeilen(antwort: WasDarfAntwort, w: Woerter, b: Betrachter,
  zusatz: { teamId: string | null; kontingent?: { grenzeProzent?: number | null; vomKontingent?: Anteil | null; kosten?: Balken | null } | null }): { erlaubt: Antwort[]; nicht: Antwort[] } {
  const anpassen = rechteAnpassenWeg(antwort.person, b);
  const alle = [
    rechercheZeile(antwort.recherche.status, { person: antwort.person, grund: antwort.recherche.grund, teamId: zusatz.teamId, b }),
    ...(zusatz.kontingent && b.lesen.includes('costs.read') ? [kontingentZeile(zusatz.kontingent, b)] : []),
    ...antwort.rechte.map(recht => rechtZeile(recht, w, anpassen)),
    zweiFaktorZeile(antwort.person.zweiFaktor, b),
  ];
  return { erlaubt: alle.filter(zeile => zeile.ja !== false), nicht: alle.filter(zeile => zeile.ja === false) };
}
